import { ChartOptions, Tick } from 'chart.js'
import { PartialEventContext } from 'chartjs-plugin-annotation'
import { getFontSize } from '../../../../../utils/getFontSize'
import { roundTicksTo1Decimal } from '../../../utils/roundTicksTo1Decimal'

interface ProductionChartOptionsProps {
	activeElement: number
	xLabels: string[]
	isDarkMode: boolean
	onHover?: (index: number) => void
}

export const getProductionChartOptions = ({
	activeElement,
	xLabels,
	isDarkMode,
	onHover,
}: ProductionChartOptionsProps): ChartOptions => {
	const leftTickColor = isDarkMode ? '#3cb49f' : '#127d27'
	const rightTickColor = isDarkMode ? '#127d27' : '#40bfa9'
	const gridColor = isDarkMode ? '#144448' : '#f5f5f5'
	const xTickColor = isDarkMode ? '#e3e3e3' : '#858585'
	const tooltipColor = isDarkMode ? '#3cb49f' : '#127d27'
	const tooltipBackgroundColor = isDarkMode ? '#0d2d30' : '#ffffff'
	const tooltipTextColor = isDarkMode ? '#e3e3e3' : '#858585'

	const fontSize = getFontSize()

	return {
		devicePixelRatio: 2,
		responsive: true,
		interaction: {
			mode: 'index',
			intersect: false,
		},
		elements: {
			point: {
				hitRadius: 5,
			},
			line: {
				tension: 0.5,
			},
		},
		layout: {
			autoPadding: true,
			padding: {
				left: 14,
				right: 14,
			},
		},
		maintainAspectRatio: false,
		scales: {
			x: {
				stacked: true,
				ticks: {
					maxTicksLimit: 6,
					minRotation: 45,
					maxRotation: 45,
					color: xTickColor,
					font: {
						size: fontSize,
					},
				},
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
			},
			y: {
				stacked: true,
				position: 'left',
				grace: '100%',
				min: 0,
				ticks: {
					color: leftTickColor,
					autoSkip: true,
					maxTicksLimit: 8,
					font: {
						size: fontSize,
					},
					callback: (tickValue: string | number, index: number, ticks: Tick[]) => {
						if (index !== 0) {
							return roundTicksTo1Decimal(tickValue, index, ticks)
						}
					},
				},
				grid: {
					color: gridColor,
					drawTicks: false,
				},
				border: {
					display: false,
				},
			},
			y1: {
				position: 'right',
				grace: '100%',
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
				suggestedMin: (context: any) => {
					const datasets = context.chart.data.datasets.filter((ds: any) => ds.yAxisID === 'y1')
					if (datasets.length > 0) {
						const allValues = datasets.flatMap((ds: any) =>
							(ds.data as (number | null | undefined)[]).filter(
								(val: any): val is number => val !== null && val !== undefined
							)
						)
						if (allValues.length > 0) {
							return Math.min(0, ...allValues)
						}
					}
					return 0
				},
				ticks: {
					color: rightTickColor,
					autoSkip: true,
					maxTicksLimit: 8,
					font: {
						size: fontSize,
					},
					callback: (tickValue: string | number, index: number, ticks: Tick[]) => {
						if (index !== 0) {
							return roundTicksTo1Decimal(tickValue, index, ticks)
						}
					},
				},
			},
		},
		plugins: {
			tooltip: {
				titleFont: {
					size: fontSize + 2,
					weight: 'bold',
				},
				bodyFont: {
					size: fontSize,
				},
				enabled: true,
				callbacks: {
					afterTitle(tooltipItems) {
						if (tooltipItems && tooltipItems.length > 0 && onHover) {
							onHover(tooltipItems[0].dataIndex)
						}
					},
				},
			},
			legend: { display: false },
			annotation: {
				clip: false,
				annotations: [
					{
						type: 'line',
						xMin: activeElement,
						xMax: activeElement,
						borderColor: tooltipColor,
						borderWidth: 2,
						drawTime: 'beforeDatasetsDraw',
						display: true,
					},
					{
						type: 'label',
						xValue: activeElement,
						yValue: (context: PartialEventContext) => context.chart.scales.y.max * 0.9,
						borderRadius: 6,
						borderWidth: 2,
						content: xLabels[activeElement],
						borderColor: tooltipColor,
						color: tooltipTextColor,
						backgroundColor: tooltipBackgroundColor,
						drawTime: 'beforeDatasetsDraw',
						display: true,
						font: {
							size: fontSize,
						},
					},
				],
			},
		},
	}
}
